// src/utils/imageUtils.js
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

// Optimized constants for faster processing
const IMAGE_CONSTANTS = {
  MAX_FILE_SIZE: 10 * 1024 * 1024,
  COMPRESSION_THRESHOLD: 1 * 1024 * 1024, // Reduced threshold to compress more files
  MAX_DIMENSION: 1200, // Further reduced for faster processing
  VALID_TYPES: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'],
  COMPRESSION_QUALITY: 0.5, // Further reduced for faster upload
  THUMBNAIL_QUALITY: 0.3, // Very low quality for thumbnails/previews
  MAX_CONCURRENT_UPLOADS: 3 // Limit concurrent uploads to prevent throttling
};

// Cache for processed images to avoid reprocessing
const processedImageCache = new Map();

// Improved file validation with more specific checks
const isValidFile = (file) => {
  // Quick size check first
  if (file.size > IMAGE_CONSTANTS.MAX_FILE_SIZE) {
    console.warn(`File too large: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
    return false;
  }
  
  // Then type check
  if (!IMAGE_CONSTANTS.VALID_TYPES.includes(file.type)) {
    console.warn(`Invalid file type: ${file.type} for ${file.name}`);
    return false;
  }
  
  return true;
};

// Significantly optimized compression function with progressive quality reduction
const compressImage = async (file, forPreview = false) => {
  // Generate a cache key based on file properties
  const cacheKey = `${file.name}-${file.size}-${forPreview}`;
  
  // Check if we already processed this file
  if (processedImageCache.has(cacheKey)) {
    return processedImageCache.get(cacheKey);
  }
  
  // Skip compression for small enough files unless it's for preview
  if (!forPreview && file.size <= IMAGE_CONSTANTS.COMPRESSION_THRESHOLD) {
    return file;
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    
    // Set a timeout to prevent hanging on image load
    const timeout = setTimeout(() => {
      URL.revokeObjectURL(url);
      reject(new Error('Image load timed out'));
    }, 10000);

    img.onload = () => {
      clearTimeout(timeout);
      URL.revokeObjectURL(url);
      
      // Use OffscreenCanvas if available for better performance
      const canvas = typeof OffscreenCanvas !== 'undefined' 
        ? new OffscreenCanvas(1, 1) 
        : document.createElement('canvas');
        
      let { width, height } = img;

      // Calculate dimensions once - more aggressive for previews
      const maxDimension = forPreview ? IMAGE_CONSTANTS.MAX_DIMENSION / 2 : IMAGE_CONSTANTS.MAX_DIMENSION;
      const ratio = Math.min(maxDimension / Math.max(width, height), 1);
      
      // Only resize if necessary
      if (ratio < 1) {
        width = Math.floor(width * ratio);
        height = Math.floor(height * ratio);
      }

      canvas.width = width;
      canvas.height = height;

      // Optimize canvas operations
      const ctx = canvas.getContext('2d', {
        alpha: false,
        willReadFrequently: false,
        desynchronized: true // Use desynchronized for speed when available
      });
      
      // Optimize rendering quality based on purpose
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = forPreview ? 'low' : 'medium';
      ctx.drawImage(img, 0, 0, width, height);

      // Use appropriate quality based on purpose
      const quality = forPreview ? 
        IMAGE_CONSTANTS.THUMBNAIL_QUALITY : 
        IMAGE_CONSTANTS.COMPRESSION_QUALITY;

      // Use createImageBitmap for faster processing if available
      if (typeof canvas.convertToBlob === 'function') {
        canvas.convertToBlob({
          type: 'image/jpeg',
          quality: quality
        }).then(blob => {
          const compressedFile = new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now()
          });
          
          // Cache the result
          processedImageCache.set(cacheKey, compressedFile);
          resolve(compressedFile);
        }).catch(reject);
      } else {
        // Fallback to traditional toBlob
        canvas.toBlob(
          blob => {
            if (canvas instanceof HTMLCanvasElement) {
              canvas.remove();
            }
            
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            
            // Cache the result
            processedImageCache.set(cacheKey, compressedFile);
            resolve(compressedFile);
          },
          'image/jpeg',
          quality
        );
      }
    };

    img.onerror = (e) => {
      clearTimeout(timeout);
      URL.revokeObjectURL(url);
      reject(new Error(`Failed to load image: ${e.message}`));
    };
    
    // Set specific loading attributes for better performance
    img.decoding = 'async';
    img.loading = 'eager';
    img.src = url;
  });
};

// Generate a preview image quickly for UI display
export const generatePreview = async (file) => {
  try {
    return await compressImage(file, true);
  } catch (error) {
    console.warn('Preview generation failed, using original file', error);
    return file;
  }
};

// Optimized upload function with retry logic
const uploadSingleFile = async (file, userId, petName, retryCount = 0) => {
  try {
    // Start compression early
    const compressedFile = await compressImage(file);
    
    // Generate a more unique filename with less randomness (faster)
    const timestamp = Date.now();
    const randomSuffix = Math.floor(Math.random() * 10000);
    const fileName = `${userId}/${timestamp}-${randomSuffix}.jpg`;

    // Use more aggressive caching
    const { error, data } = await supabase.storage
      .from('dog-images')
      .upload(fileName, compressedFile, {
        cacheControl: '3600',
        upsert: true,
        contentType: 'image/jpeg'
      });

    if (error) {
      // Implement retry logic for common upload errors
      if (retryCount < 2 && (error.statusCode === 503 || error.statusCode === 429)) {
        console.warn(`Retrying upload for ${file.name} (attempt ${retryCount + 1})`);
        // Exponential backoff
        await new Promise(r => setTimeout(r, 1000 * Math.pow(2, retryCount)));
        return uploadSingleFile(file, userId, petName, retryCount + 1);
      }
      throw error;
    }

    // Get public URL more efficiently
    return supabase.storage
      .from('dog-images')
      .getPublicUrl(fileName)
      .data.publicUrl;

  } catch (error) {
    console.error(`Upload failed for ${file.name}:`, error);
    throw error;
  }
};

// Improved batch upload with concurrency control
export const uploadImages = async (files, userId, petName) => {
  const startTime = performance.now();

  try {
    // Filter valid files first
    const validFiles = Array.from(files).filter(isValidFile);
    
    if (!validFiles.length) {
      throw new Error('No valid files to upload');
    }

    // Clear cache if it gets too large
    if (processedImageCache.size > 20) {
      processedImageCache.clear();
    }

    // Use a concurrency-controlled upload approach
    const results = [];
    const uploadQueue = [...validFiles];
    const inProgress = new Set();
    
    // Process uploads with limited concurrency
    const processQueue = async () => {
      if (uploadQueue.length === 0) return;
      
      // Don't exceed max concurrent uploads
      if (inProgress.size >= IMAGE_CONSTANTS.MAX_CONCURRENT_UPLOADS) return;
      
      const file = uploadQueue.shift();
      inProgress.add(file);
      
      try {
        const url = await uploadSingleFile(file, userId, petName);
        results.push(url);
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
      } finally {
        inProgress.delete(file);
        // Continue processing queue
        await processQueue();
      }
    };
    
    // Start initial batch of uploads
    const initialBatch = Math.min(IMAGE_CONSTANTS.MAX_CONCURRENT_UPLOADS, uploadQueue.length);
    const initialUploads = Array(initialBatch).fill().map(() => processQueue());
    
    // Wait for all uploads to complete
    await Promise.all(initialUploads);
    
    // Wait for any remaining in-progress uploads
    while (inProgress.size > 0) {
      await new Promise(r => setTimeout(r, 100));
    }

    const duration = (performance.now() - startTime) / 1000;
    console.log(`📸 Uploaded ${results.length}/${files.length} images in ${duration.toFixed(2)}s`);

    return results;
  } catch (error) {
    console.error('Upload failed:', error);
    throw error;
  }
};

// Cleanup function to free memory
export const clearImageCache = () => {
  processedImageCache.clear();
};